import { apiSend, USE_MOCK_API } from './httpClient'
import { getTrip, updateTrip } from './trips.mock'
import { STORE_KEYS, readCollection, writeCollection, ApiError } from './store'
import { addDays, tripDayCount } from '../lib/dates'
import type { TripPoint } from './types'

export interface DateChangeImpact {
  point_id: string
  title: string
  fixed_date: string | null
  old_day_index: number
  new_day_index: number
  outside: boolean
}
export interface DateChangePreview {
  start_date: string
  end_date: string
  expected_updated_at: string
  impacts: DateChangeImpact[]
  has_conflicts: boolean
}

export async function previewDateChange(tripId: string, start_date: string, end_date: string): Promise<DateChangePreview> {
  if (!USE_MOCK_API) return apiSend('POST', `/trips/${tripId}/date-change/preview`, { start_date, end_date })
  const trip = await getTrip(tripId)
  const last = tripDayCount(start_date, end_date) - 1
  const impacts = readCollection<TripPoint>(STORE_KEYS.points).filter(p => p.trip_id === tripId).map(p => {
    const fixed = p.timing === 'exact' && p.date ? p.date : null
    const outside = fixed ? fixed < start_date || fixed > end_date : p.day_index > last
    return { point_id: p.id, title: p.title, fixed_date: fixed, old_day_index: p.day_index,
      new_day_index: outside ? -1 : fixed ? tripDayCount(start_date, fixed) - 1 : p.day_index, outside }
  })
  return { start_date, end_date, expected_updated_at: trip.updated_at, impacts,
    has_conflicts: impacts.some(i => i.outside) }
}

export async function applyDateChange(tripId: string, preview: DateChangePreview, out_of_range: 'cancel' | 'keep_unscheduled'): Promise<void> {
  if (!USE_MOCK_API) {
    await apiSend('POST', `/trips/${tripId}/date-change/apply`, { ...preview, out_of_range })
    return
  }
  const trip = await getTrip(tripId)
  if (trip.updated_at !== preview.expected_updated_at) throw new ApiError(409, 'Trip changed; preview again')
  if (preview.has_conflicts && out_of_range !== 'keep_unscheduled') throw new ApiError(409, 'Resolve outside visits')
  const delta = trip.start_date ? Math.round((Date.parse(preview.start_date) - Date.parse(trip.start_date)) / 86400000) : 0
  const byId = new Map(preview.impacts.map(i => [i.point_id, i]))
  const points = readCollection<TripPoint>(STORE_KEYS.points).map(p => {
    if (p.trip_id !== tripId) return p
    const impact = byId.get(p.id)
    return { ...p, day_index: impact?.new_day_index ?? p.day_index,
      window_start: p.window_start ? addDays(p.window_start, delta) : p.window_start,
      window_end: p.window_end ? addDays(p.window_end, delta) : p.window_end }
  })
  writeCollection(STORE_KEYS.points, points)
  await updateTrip(tripId, { start_date: preview.start_date, end_date: preview.end_date })
}
